import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { showsService } from '../utils/showsService.js';

// Style constants for consistent theming
const COLORS = {
  primary: 'var(--primary-color, #667eea)',
  primaryDark: 'var(--primary-dark, #5a6fd8)',
  textPrimary: 'var(--color-text-primary, #111827)',
  textSecondary: 'var(--color-text-secondary, #6B7280)',
  textMuted: '#64748b',
  border: 'var(--color-border, #E5E7EB)',
  bgSecondary: 'var(--color-bg-secondary, #F9FAFB)',
  error: '#991b1b'
};

const TRANSITIONS = {
  smooth: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  fast: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  slow: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)'
};

const GalleryPage = ({ initialFilter = null }) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [animateIn, setAnimateIn] = useState(false);
  const [galleryData, setGalleryData] = useState([]);
  const [availableShows, setAvailableShows] = useState([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const galleryRef = useRef(null);

  // Fetch gallery data from shows service
  useEffect(() => {
    const loadGalleryData = async () => {
      try {
        setIsLoading(true);
        const images = await showsService.getGalleryImages();
        
        setGalleryData(images);
        
        // Get unique shows from gallery data
        const shows = [...new Set(images.map(item => item.show))];
        setAvailableShows(shows.sort());
        
        setIsLoading(false);
        setTimeout(() => setAnimateIn(true), 200);
      } catch (error) {
        console.error('Error loading gallery data:', error);
        setIsLoading(false);
      }
    };
    
    loadGalleryData();
  }, []);

  // Filter gallery items based on selected filter
  const filteredItems = useMemo(() => {
    if (selectedFilter === 'all') return galleryData;
    return galleryData.filter(item => item.show === selectedFilter);
  }, [selectedFilter, galleryData]);

  const groupedShows = useMemo(() => {
    const groups = new Map();

    filteredItems.forEach((item) => {
      if (!groups.has(item.show)) {
        groups.set(item.show, {
          show: item.show,
          showName: item.showName || item.show,
          items: []
        });
      }

      groups.get(item.show).items.push(item);
    });

    const orderMap = new Map(availableShows.map((show, index) => [show, index]));

    return Array.from(groups.values()).sort((a, b) => {
      const orderA = orderMap.has(a.show) ? orderMap.get(a.show) : Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.has(b.show) ? orderMap.get(b.show) : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return String(a.showName).localeCompare(String(b.showName));
    });
  }, [filteredItems, availableShows]);

  const showNameBySlug = useMemo(() => {
    const map = new Map();
    galleryData.forEach((item) => {
      if (!map.has(item.show)) {
        map.set(item.show, item.showName || item.show);
      }
    });
    return map;
  }, [galleryData]);

  const filteredShowOptions = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();

    return availableShows
      .map((show) => ({
        value: show,
        label: showNameBySlug.get(show) || show
      }))
      .filter((option) => {
        if (!query) return true;
        return option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableShows, showNameBySlug, filterQuery]);

  const selectedFilterLabel = useMemo(() => {
    if (selectedFilter === 'all') return t('All Shows');
    return showNameBySlug.get(selectedFilter) || selectedFilter;
  }, [selectedFilter, showNameBySlug, t]);

  // Handle URL parameters for initial filter
  useEffect(() => {
    if (!galleryData.length) return;
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const showFilter = urlParams.get('show');
      
      // Sanitize and validate filter value
      if (showFilter && typeof showFilter === 'string' && availableShows.includes(showFilter)) {
        setSelectedFilter(showFilter);
      } else if (initialFilter && availableShows.includes(initialFilter)) {
        setSelectedFilter(initialFilter);
      }
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
    }
  }, [initialFilter, availableShows, galleryData]);

  // Update URL when filter changes
  const handleFilterChange = useCallback((filter) => {
    setSelectedFilter(filter);
    setIsFilterPanelOpen(false);
    setFilterQuery('');
    
    // Update URL without page reload
    const url = new URL(window.location);
    if (filter === 'all') {
      url.searchParams.delete('show');
    } else {
      url.searchParams.set('show', filter);
    }
    window.history.replaceState({}, '', url);

    // Animate items out and in
    setAnimateIn(false);
    setTimeout(() => setAnimateIn(true), 150);
  }, []);

  // Handle image click for lightbox
  const handleImageClick = useCallback((image) => {
    const index = filteredItems.findIndex(item => item.id === image.id);
    setSelectedImage(image);
    setSelectedImageIndex(index);
    document.body.style.overflow = 'hidden';
  }, [filteredItems]);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    setSelectedImageIndex(-1);
    document.body.style.overflow = '';
  }, []);

  // Navigate lightbox prev/next
  const navigateLightbox = useCallback((dir) => {
    setSelectedImageIndex(prev => {
      const next = prev + dir;
      if (next < 0 || next >= filteredItems.length) return prev;
      setSelectedImage(filteredItems[next]);
      return next;
    });
  }, [filteredItems]);

  // Handle keyboard for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') navigateLightbox(1);
      else if (e.key === 'ArrowLeft') navigateLightbox(-1);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, closeLightbox, navigateLightbox]);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#6b7280',
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            {t('Loading gallery...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="gallery-page-shell"
      style={{
        background: 'var(--gal-surface, var(--surface-elevated, #ffffff))',
        minHeight: '100vh',
        paddingBottom: '4rem'
      }}
    >
      {/* Hero Section with gradient accent */}
        <div style={{
        background: 'linear-gradient(180deg, var(--gal-surface, #ffffff) 0%, var(--gal-surface-soft, #f7f9fb) 55%, var(--gal-surface, #ffffff) 100%)',
        color: 'var(--gal-text)',
        textAlign: 'center',
        padding: '5rem 2rem 4rem',
        marginBottom: '4rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.06) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)',
          opacity: 0.6,
          pointerEvents: 'none'
        }}></div>
        
        {/* Colorful accent line */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899, #f59e0b)',
          opacity: 0.8,
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
        }}></div>
        
        <div style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 'var(--max-width, 1320px)',
          margin: '0 auto'
        }}>
          <h1 style={{
            fontSize: 'clamp(2.75rem, 5.5vw, 4rem)',
            fontWeight: 800,
            margin: '0 0 1.25rem 0',
            letterSpacing: '-0.025em',
            background: 'linear-gradient(135deg, var(--gal-text, #111827) 0%, var(--gal-hero-title-end, #4b5563) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            opacity: animateIn ? 1 : 0,
            transform: animateIn ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)'
          }}>
            {t('Gallery')}
          </h1>
          <p style={{
            fontSize: '1.1rem',
            opacity: animateIn ? 0.7 : 0,
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6,
            color: 'var(--gal-muted)',
            transform: animateIn ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) 0.2s'
          }}>
            {t('gallery.description')}
          </p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="gallery-filter-wrap" style={{
        opacity: animateIn ? 1 : 0,
        transform: animateIn ? 'translateY(0)' : 'translateY(18px)',
        transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1) 0.25s'
      }}>
        <div className="gallery-filter-card">
          <div className="gallery-filter-head">
            <div>
              <p className="gallery-filter-title">{t('gallery.filter_by_show')}</p>
              <p className="gallery-filter-subtitle">
                {selectedFilter === 'all'
                  ? `${availableShows.length} ${t('shows', 'shows')} available`
                  : `${t('Now viewing', 'Now viewing')}: ${selectedFilterLabel}`}
              </p>
            </div>
            <button
              type="button"
              className="gallery-filter-toggle"
              onClick={() => setIsFilterPanelOpen(prev => !prev)}
              aria-expanded={isFilterPanelOpen}
              aria-controls="gallery-filter-panel"
            >
              {isFilterPanelOpen ? t('Hide filters', 'Hide filters') : t('Browse shows', 'Browse shows')}
            </button>
          </div>

          {selectedFilter !== 'all' && !isFilterPanelOpen && (
            <div className="gallery-filter-selected-row">
              <FilterButton
                isActive
                onClick={() => handleFilterChange('all')}
                delay={0}
                animateIn
                compact
              >
                {selectedFilterLabel}
              </FilterButton>
              <button
                type="button"
                className="gallery-filter-clear"
                onClick={() => handleFilterChange('all')}
              >
                {t('Clear filter', 'Clear filter')}
              </button>
            </div>
          )}

          {isFilterPanelOpen && (
            <div id="gallery-filter-panel" className="gallery-filter-panel">
              <input
                type="text"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="gallery-filter-search"
                placeholder={t('Search show...', 'Search show...')}
                aria-label={t('Search show', 'Search show')}
              />

              <div className="gallery-filter-grid">
                <FilterButton
                  key="all"
                  isActive={selectedFilter === 'all'}
                  onClick={() => handleFilterChange('all')}
                  delay={0}
                  animateIn
                  compact
                >
                  {t('All Shows')}
                </FilterButton>

                {filteredShowOptions.map((option) => (
                  <FilterButton
                    key={option.value}
                    isActive={selectedFilter === option.value}
                    onClick={() => handleFilterChange(option.value)}
                    delay={0}
                    animateIn
                    compact
                  >
                    {option.label}
                  </FilterButton>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery By Show (title + carousel per show) */}
      <div style={{
        maxWidth: 'var(--max-width, 1320px)',
        margin: '0 auto',
        padding: '0 2rem',
        overflow: 'hidden'
      }}>
        <div className="gallery-show-sections" style={{ marginBottom: '4rem' }}>
          {groupedShows.map((group, groupIndex) => (
            <ShowGallerySection
              key={group.show}
              group={group}
              groupIndex={groupIndex}
              animateIn={animateIn}
              onImageClick={handleImageClick}
            />
          ))}
        </div>

        {/* Empty State */}
        {groupedShows.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 0',
            color: 'var(--color-text-secondary, #6B7280)',
            opacity: animateIn ? 1 : 0,
            transform: animateIn ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1) 0.3s'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              margin: '0 0 1rem 0',
              color: 'var(--color-text-primary, #111827)'
            }}>
              {t('gallery.no_images')}
            </h3>
            <p style={{
              fontSize: '1.1rem',
              maxWidth: '400px',
              margin: '0 auto',
              lineHeight: 1.6
            }}>
              {t('gallery.no_images_description')}
            </p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <Lightbox
          image={selectedImage}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          hasPrev={selectedImageIndex > 0}
          hasNext={selectedImageIndex < filteredItems.length - 1}
        />
      )}

      {/* Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .gallery-page-shell {
            overflow-x: hidden;
            --gal-surface: var(--color-bg-primary, #ffffff);
            --gal-surface-soft: var(--color-bg-secondary, #f7f9fb);
            --gal-text: var(--color-text-primary, #0f172a);
            --gal-muted: var(--color-text-secondary, #4b5563);
            --gal-border: var(--color-border, #e5e7eb);
            --gal-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
            --gal-shadow-strong: 0 18px 40px rgba(15, 23, 42, 0.14);
            --gal-caption-bg: #ffffff;
            --gal-caption-title: #0f172a;
            --gal-caption-meta: #475569;
            --gal-hero-title-end: #4b5563;
          }

          /* Force explicit light-mode tokens to avoid inherited dark palette variables */
          [data-theme="light"] .gallery-page-shell,
          :root:not([data-theme="dark"]) .gallery-page-shell {
            --gal-surface: #ffffff;
            --gal-surface-soft: #f8fafc;
            --gal-text: #0f172a;
            --gal-muted: #475569;
            --gal-border: #dbe3ee;
            --gal-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
            --gal-shadow-strong: 0 14px 30px rgba(15, 23, 42, 0.1);
            --gal-caption-bg: #ffffff;
            --gal-caption-title: #0f172a;
            --gal-caption-meta: #475569;
            --gal-hero-title-end: #4b5563;
          }

          [data-theme="dark"] .gallery-page-shell {
            --gal-surface: color-mix(in srgb, #0b1220 92%, transparent);
            --gal-surface-soft: color-mix(in srgb, #0f172a 94%, transparent);
            --gal-text: #ffffff;
            --gal-muted: #ffffff;
            --gal-border: color-mix(in srgb, #1f2937 80%, transparent);
            --gal-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
            --gal-shadow-strong: 0 18px 40px rgba(0, 0, 0, 0.48);
            --gal-caption-bg: transparent;
            --gal-caption-title: #ffffff;
            --gal-caption-meta: #ffffff;
            --gal-hero-title-end: #ffffff;
          }

          [data-theme="dark"] .gallery-page-shell,
          [data-theme="dark"] .gallery-page-shell p,
          [data-theme="dark"] .gallery-page-shell span,
          [data-theme="dark"] .gallery-page-shell h1,
          [data-theme="dark"] .gallery-page-shell h2,
          [data-theme="dark"] .gallery-page-shell h3 {
            color: #ffffff;
          }

          .gallery-filter-wrap {
            max-width: var(--max-width, 1320px);
            margin: 0 auto 2.75rem;
            padding: 0 2rem;
          }

          .gallery-filter-card {
            background: linear-gradient(180deg, color-mix(in srgb, var(--gal-surface) 96%, transparent), color-mix(in srgb, var(--gal-surface-soft) 98%, transparent));
            border: 1px solid color-mix(in srgb, var(--gal-border) 80%, transparent);
            border-radius: 18px;
            padding: 1.2rem;
            box-shadow: var(--gal-shadow);
          }

          .gallery-filter-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 1rem;
          }

          .gallery-filter-title {
            margin: 0;
            font-size: 1.05rem;
            font-weight: 800;
            color: var(--gal-text);
          }

          .gallery-filter-subtitle {
            margin: 0.35rem 0 0;
            font-size: 0.92rem;
            color: var(--gal-muted);
            opacity: 0.9;
          }

          .gallery-filter-toggle {
            appearance: none;
            border: 1px solid color-mix(in srgb, var(--gal-border) 84%, transparent);
            background: color-mix(in srgb, var(--gal-surface) 80%, #3b82f6 20%);
            color: var(--gal-text);
            border-radius: 999px;
            padding: 0.58rem 1rem;
            font-size: 0.85rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .gallery-filter-toggle:hover {
            transform: translateY(-1px);
            box-shadow: var(--gal-shadow);
          }

          .gallery-filter-selected-row {
            margin-top: 0.95rem;
            display: flex;
            align-items: center;
            gap: 0.7rem;
            flex-wrap: wrap;
          }

          .gallery-filter-clear {
            appearance: none;
            border: 1px solid color-mix(in srgb, var(--gal-border) 84%, transparent);
            background: transparent;
            color: var(--gal-muted);
            border-radius: 999px;
            padding: 0.5rem 0.85rem;
            font-size: 0.8rem;
            font-weight: 600;
            cursor: pointer;
          }

          .gallery-filter-panel {
            margin-top: 1rem;
            display: grid;
            gap: 0.8rem;
          }

          .gallery-filter-search {
            width: 100%;
            border-radius: 11px;
            border: 1px solid color-mix(in srgb, var(--gal-border) 82%, transparent);
            padding: 0.62rem 0.85rem;
            font-size: 0.9rem;
            font-weight: 500;
            color: var(--gal-text);
            background: var(--gal-surface);
            outline: none;
          }

          .gallery-filter-search:focus {
            border-color: color-mix(in srgb, #3b82f6 55%, var(--gal-border));
            box-shadow: 0 0 0 3px color-mix(in srgb, #3b82f6 22%, transparent);
          }

          .gallery-filter-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
            gap: 0.55rem;
            max-height: 360px;
            overflow: auto;
            padding-right: 0.2rem;
          }

          .gallery-item-media {
            border: 0 !important;
            box-shadow: none !important;
          }

          .gallery-item-caption {
            background: var(--gal-caption-bg) !important;
            border: 0 !important;
          }

          .gallery-item-title {
            color: var(--gal-caption-title) !important;
            -webkit-text-fill-color: var(--gal-caption-title) !important;
            opacity: 1 !important;
          }

          .gallery-item-meta {
            color: var(--gal-caption-meta) !important;
            -webkit-text-fill-color: var(--gal-caption-meta) !important;
            opacity: 1 !important;
          }

          .gallery-show-sections {
            display: grid;
            gap: clamp(2.2rem, 4vw, 3.6rem);
          }

          .gallery-show-section {
            display: grid;
            gap: 1rem;
          }

          .gallery-show-heading {
            margin: 0;
            font-size: clamp(1.35rem, 2.4vw, 1.95rem);
            font-weight: 800;
            line-height: 1.2;
            letter-spacing: -0.01em;
            color: var(--gal-text);
          }

          .gallery-show-shell {
            position: relative;
            width: 100%;
            overflow: hidden;
          }

          .gallery-show-rail {
            width: 100%;
            min-width: 0;
            display: flex;
            flex-wrap: nowrap;
            gap: 1.1rem;
            overflow-x: auto;
            padding: 0.2rem 0.1rem 0.35rem;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            scrollbar-width: none;
          }

          .gallery-show-rail::-webkit-scrollbar {
            display: none;
          }

          .gallery-show-slide {
            flex: 0 0 clamp(220px, 22vw, 300px);
            scroll-snap-align: start;
          }

          .gallery-show-arrow {
            appearance: none;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            z-index: 4;
            width: 58px;
            height: 58px;
            border-radius: 999px;
            border: 1px solid color-mix(in srgb, var(--gal-border) 72%, transparent);
            background: color-mix(in srgb, var(--gal-surface) 78%, #0f172a 22%);
            color: var(--gal-text);
            cursor: pointer;
            box-shadow: 0 18px 40px rgba(15, 23, 42, 0.24);
            backdrop-filter: blur(8px);
            font-size: 2rem;
            font-weight: 700;
            line-height: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
          }

          .gallery-show-arrow:hover {
            transform: translateY(-50%) scale(1.06);
            box-shadow: 0 22px 46px rgba(15, 23, 42, 0.3);
          }

          .gallery-show-arrow-prev {
            left: 0.8rem;
          }

          .gallery-show-arrow-next {
            right: 0.8rem;
          }

          [data-theme="dark"] .gallery-show-arrow {
            background: color-mix(in srgb, #020617 76%, #1e293b 24%);
            color: #ffffff;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .gallery-filter-wrap {
              padding: 0 1rem;
              margin-bottom: 2rem;
            }

            .gallery-filter-head {
              flex-direction: column;
              align-items: stretch;
            }

            .gallery-filter-grid {
              grid-template-columns: 1fr;
            }
            
            .gallery-show-slide {
              flex-basis: clamp(235px, 62vw, 360px);
            }

            .gallery-show-arrow {
              width: 50px;
              height: 50px;
              font-size: 1.75rem;
            }
          }
          
          @media (max-width: 480px) {
            .gallery-show-slide {
              flex-basis: 78vw;
            }

            .gallery-show-arrow {
              width: 44px;
              height: 44px;
              font-size: 1.5rem;
            }

            .gallery-show-arrow-prev {
              left: 0.35rem;
            }

            .gallery-show-arrow-next {
              right: 0.35rem;
            }
          }
        `
      }} />
    </div>
  );
};

// Filter Button Component
const FilterButton = React.memo(({ children, isActive, onClick, delay, animateIn, compact = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-pressed={isActive}
      aria-label={`Filter by ${children}`}
      style={{
        padding: compact ? '0.58rem 0.82rem' : '0.72rem 1.15rem',
        background: isActive
          ? 'linear-gradient(135deg, #1d4ed8, #2563eb)'
          : (isHovered ? 'var(--gal-surface-soft, #f7f9fb)' : 'var(--gal-surface, #ffffff)'),
        border: isActive ? '1px solid transparent' : '1px solid var(--gal-border, #e5e7eb)',
        borderRadius: '11px',
        color: isActive ? '#ffffff' : 'var(--gal-text, #0f172a)',
        WebkitTextFillColor: isActive ? '#ffffff' : 'var(--gal-text, #0f172a)',
        fontWeight: 700,
        fontSize: compact ? '0.84rem' : '0.9rem',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        minHeight: compact ? '40px' : '46px',
        width: compact ? '100%' : 'auto',
        transition: 'all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1)',
        boxShadow: isActive || isHovered
          ? '0 9px 20px rgba(15, 23, 42, 0.14)'
          : 'none',
        opacity: animateIn ? 1 : 0,
        transform: animateIn
          ? (isHovered ? 'translateY(-1px)' : 'translateY(0)')
          : 'translateY(20px) scale(0.95)',
        transitionDelay: `${delay}s`,
        transitionProperty: 'opacity, transform, box-shadow, background, color, border',
        letterSpacing: '0.01em',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          color: 'inherit',
          WebkitTextFillColor: 'currentColor'
        }}
      >
        {children}
      </span>
    </button>
  );
});

const ShowGallerySection = React.memo(({ group, groupIndex, animateIn, onImageClick }) => {
  const railRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(group.items.length > 1);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(group.items.length > 1);

  const updateScrollState = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const hasScrollableOverflow = rail.scrollWidth - rail.clientWidth > 6;
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
    setHasOverflow(hasScrollableOverflow);
    setCanPrev(rail.scrollLeft > 2);
    setCanNext(hasScrollableOverflow && rail.scrollLeft < maxScroll);
  }, []);

  useEffect(() => {
    updateScrollState();
    const rail = railRef.current;
    if (!rail) return undefined;

    rail.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      rail.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, group.items.length]);

  const scrollByStep = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const firstItem = rail.querySelector('.gallery-show-slide');
    const firstItemWidth = firstItem?.getBoundingClientRect?.().width || rail.clientWidth * 0.8;
    const gap = parseFloat(window.getComputedStyle(rail).gap || '0') || 0;
    rail.scrollBy({ left: direction * (firstItemWidth + gap), behavior: 'smooth' });
  };

  return (
    <section
      className="gallery-show-section"
      style={{
        opacity: animateIn ? 1 : 0,
        transform: animateIn ? 'translateY(0)' : 'translateY(14px)',
        transition: `all 0.45s cubic-bezier(0.25, 0.8, 0.25, 1) ${0.1 + groupIndex * 0.05}s`
      }}
    >
      <h2 className="gallery-show-heading">{group.showName}</h2>

      <div className="gallery-show-shell">
        {hasOverflow && canPrev && (
          <button
            className="gallery-show-arrow gallery-show-arrow-prev"
            type="button"
            aria-label={`Previous ${group.showName} image`}
            onClick={() => scrollByStep(-1)}
          >
            ‹
          </button>
        )}

        <div className="gallery-show-rail" ref={railRef}>
          {group.items.map((item, index) => (
            <div className="gallery-show-slide" key={item.id}>
              <GalleryItem
                item={item}
                index={index}
                onClick={onImageClick}
                animateIn={animateIn}
              />
            </div>
          ))}
        </div>

        {hasOverflow && canNext && (
          <button
            className="gallery-show-arrow gallery-show-arrow-next"
            type="button"
            aria-label={`Next ${group.showName} image`}
            onClick={() => scrollByStep(1)}
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
});
// Gallery Item Component
const GalleryItem = React.memo(({ item, index, onClick, animateIn }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div
      onClick={() => onClick(item)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      aria-label={`View ${item.title}`}
      onKeyPress={(e) => e.key === 'Enter' && onClick(item)}
      style={{
        position: 'relative',
        borderRadius: '0',
        overflow: 'visible',
        background: 'transparent',
        boxShadow: 'none',
        transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.45s ease',
        cursor: 'pointer',
        opacity: animateIn ? 1 : 0,
        transform: animateIn ? 
          (isHovered ? 'translateY(-6px)' : 'translateY(0)') : 
          'translateY(30px) scale(0.95)',
        transitionDelay: `${index * 0.08}s`,
        willChange: isHovered ? 'transform' : 'auto'
      }}
    >
      <div style={{
        position: 'relative',
        width: '100%',
        height: '240px',
        overflow: 'hidden',
        borderRadius: '12px 12px 0 0',
        border: '0',
        borderBottom: '0',
        background: 'linear-gradient(135deg, var(--gal-surface-soft, #f8fafc) 0%, var(--gal-surface, #ffffff) 100%)'
      }} className="gallery-item-media">
        <img
          src={item.src}
          alt={item.photographer}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1), filter 0.45s ease',
            transform: isHovered ? 'scale(1.04)' : 'scale(1)',
            filter: isHovered ? 'brightness(1.04)' : 'brightness(1)',
            opacity: imageLoaded ? 1 : 0,
            willChange: isHovered ? 'transform' : 'auto'
          }}
        />
        
        {/* Loading placeholder */}
        {!imageLoaded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 37%, #e2e8f0 63%)',
            backgroundSize: '400% 100%',
            animation: 'shimmer 1.5s ease-in-out infinite'
          }} />
        )}
      </div>

      <div style={{
        padding: '0.9rem 1rem 1rem',
        color: 'var(--gal-text, #0f172a)',
        background: 'var(--gal-caption-bg, #ffffff)',
        border: '0',
        borderTop: '0',
        borderRadius: '0 0 12px 12px'
      }} className="gallery-item-caption">
        <p style={{
          fontSize: '0.9rem',
          margin: '0 0 0.3rem 0',
          fontWeight: 600,
          color: 'var(--gal-caption-meta, #475569)',
          WebkitTextFillColor: 'var(--gal-caption-meta, #475569)'
        }} className="gallery-item-meta">
          {item.showName}
        </p>
        <h3 style={{
          fontSize: '1.05rem',
          fontWeight: 700,
          margin: 0,
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
          color: 'var(--gal-caption-title, #0f172a)',
          WebkitTextFillColor: 'var(--gal-caption-title, #0f172a)'
        }} className="gallery-item-title">
          {item.title}
        </h3>
      </div>
    </div>
  );
});

// Lightbox Component
const Lightbox = React.memo(({ image, onClose, onPrev, onNext, hasPrev, hasNext }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const isDarkTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const lightboxTheme = isDarkTheme ? {
    overlayBackground: 'linear-gradient(135deg, rgba(0, 0, 0, 0.92) 0%, rgba(15, 23, 42, 0.95) 100%)',
    panelBackground: '#ffffff',
    panelShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    imageBackground: 'rgba(17,24,39,1)',
    infoBackground: 'linear-gradient(180deg, #0b1220 0%, #111827 100%)',
    infoBorder: '1px solid rgba(255, 255, 255, 0.08)',
    headingColor: '#ffffff',
    cardBackground: 'rgba(255, 255, 255, 0.04)',
    cardBorder: '1px solid rgba(255, 255, 255, 0.08)',
    labelColor: '#ffffff',
    valueColor: '#ffffff',
    dividerColor: '1px solid rgba(255, 255, 255, 0.12)',
    chipBackground: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    chipColor: '#ffffff',
    closeBackground: 'rgba(0, 0, 0, 0.75)',
    closeBorder: '2px solid rgba(255, 255, 255, 0.1)',
    closeColor: '#ffffff',
    mobileBorderTop: '#e2e8f0'
  } : {
    overlayBackground: 'linear-gradient(135deg, rgba(248, 250, 252, 0.94) 0%, rgba(226, 232, 240, 0.96) 100%)',
    panelBackground: '#ffffff',
    panelShadow: '0 25px 60px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.06)',
    imageBackground: '#f3f4f6',
    infoBackground: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    infoBorder: '1px solid rgba(15, 23, 42, 0.08)',
    headingColor: '#0f172a',
    cardBackground: 'rgba(255, 255, 255, 0.95)',
    cardBorder: '1px solid rgba(15, 23, 42, 0.08)',
    labelColor: '#334155',
    valueColor: '#0f172a',
    dividerColor: '1px solid rgba(15, 23, 42, 0.08)',
    chipBackground: '#e0e7ff',
    chipColor: '#1e3a8a',
    closeBackground: 'rgba(255, 255, 255, 0.92)',
    closeBorder: '2px solid rgba(15, 23, 42, 0.08)',
    closeColor: '#0f172a',
    mobileBorderTop: '#e2e8f0'
  };

  useEffect(() => {
    setImageLoaded(false);
  }, [image]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && hasNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && hasPrev) {
        onPrev();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // Trap focus within lightbox
    const focusableElements = document.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lightbox-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: lightboxTheme.overlayBackground,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '2rem',
        animation: 'fadeIn 0.3s ease-out'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="lightbox-content"
        style={{
          position: 'relative',
          maxWidth: '1400px',
          width: '92vw',
          maxHeight: '92vh',
          background: lightboxTheme.panelBackground,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: lightboxTheme.panelShadow,
          animation: 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        {/* Left side - Image */}
        <div className="lightbox-image" style={{
          flex: '1',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: lightboxTheme.imageBackground,
          minHeight: '400px'
        }}>
          <img
            src={image.src}
            alt={image.alt}
            loading="eager"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-out'
            }}
          />
          
          {!imageLoaded && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}

          {/* Prev/Next navigation buttons */}
          {hasPrev && (
            <button
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              aria-label="Previous image"
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: isDarkTheme ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.88)',
                border: isDarkTheme ? '2px solid rgba(255,255,255,0.15)' : '2px solid rgba(15,23,42,0.1)',
                color: isDarkTheme ? '#fff' : '#0f172a',
                fontSize: '1.75rem',
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
                backdropFilter: 'blur(4px)',
                transition: 'background 0.2s ease'
              }}
            >
              ‹
            </button>
          )}
          {hasNext && (
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              aria-label="Next image"
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: isDarkTheme ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.88)',
                border: isDarkTheme ? '2px solid rgba(255,255,255,0.15)' : '2px solid rgba(15,23,42,0.1)',
                color: isDarkTheme ? '#fff' : '#0f172a',
                fontSize: '1.75rem',
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
                backdropFilter: 'blur(4px)',
                transition: 'background 0.2s ease'
              }}
            >
              ›
            </button>
          )}
        </div>

        {/* Right side - Information */}
        <div className="lightbox-info" style={{
          flex: '0 0 380px',
          padding: '3.5rem 2.5rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '2.5rem',
          background: lightboxTheme.infoBackground,
          borderLeft: lightboxTheme.infoBorder,
          overflowY: 'auto',
          maxHeight: '92vh',
          position: 'relative'
        }}>
          {/* Decorative gradient accent */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899)'
          }}></div>
          
          <div>
            <h3 id="lightbox-title" style={{
              fontSize: '2rem',
              fontWeight: 800,
              margin: '0 0 1.75rem 0',
              color: lightboxTheme.headingColor,
              lineHeight: 1.2,
              letterSpacing: '-0.02em'
            }}>
              {image.showName}
            </h3>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
              padding: '1.5rem',
              background: lightboxTheme.cardBackground,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
              border: lightboxTheme.cardBorder
            }}>
              <div>
                <span style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: lightboxTheme.labelColor,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.6rem'
                }}>
                  {t('gallery.photographer', 'Photography')}
                </span>
                <span style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: lightboxTheme.valueColor,
                  display: 'block'
                }}>
                  {image.photographer || image.title}
                </span>
              </div>
              
              <div style={{
                borderTop: lightboxTheme.dividerColor,
                paddingTop: '1.5rem'
              }}>
                <span style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: lightboxTheme.labelColor,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.6rem'
                }}>
                  {t('gallery.category', 'Category')}
                </span>
                <span style={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  display: 'inline-block',
                  padding: '0.5rem 1rem',
                  background: lightboxTheme.chipBackground,
                  color: lightboxTheme.chipColor,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}>
                  {t(`gallery.category.${image.category}`, image.category)}
                </span>
              </div>
              
              {/* Photographer Section */}
              {image.photographer ? (
                <div>
                  <span style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: lightboxTheme.labelColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.5rem'
                  }}>
                    {t('gallery.photographer', 'Photographer')}
                  </span>
                  <span style={{
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: lightboxTheme.valueColor
                  }}>
                    {image.photographer}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          aria-label={t('gallery.close', 'Close')}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: lightboxTheme.closeBackground,
            backdropFilter: 'blur(8px)',
            border: lightboxTheme.closeBorder,
            color: lightboxTheme.closeColor,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.75rem',
            fontWeight: 300,
            transition: 'all 0.25s ease-out',
            zIndex: 10,
            lineHeight: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #8b5cf6)';
            e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
            e.currentTarget.style.borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(59, 130, 246, 0.32)';
            e.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = lightboxTheme.closeBackground;
            e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            e.currentTarget.style.borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)';
            e.currentTarget.style.color = lightboxTheme.closeColor;
          }}
        >
          ×
        </button>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          
          @media (max-width: 768px) {
            .lightbox-content {
              flex-direction: column !important;
              max-height: 100vh !important;
            }
            
            .lightbox-info {
              flex: 1 !important;
              border-left: none !important;
              border-top: 1px solid ${lightboxTheme.mobileBorderTop} !important;
              padding: 2rem 1.5rem !important;
              max-height: 50vh !important;
            }
            
            .lightbox-image {
              flex: 1 !important;
              max-height: 50vh !important;
            }
          }
          
          @media (max-width: 480px) {
            .lightbox-info {
              padding: 1.5rem 1rem !important;
              font-size: 0.9rem !important;
            }
            
            .lightbox-info h3 {
              font-size: 1.4rem !important;
            }
          }
        `
      }} />
    </div>
  );
});

export default GalleryPage;